import type { Locale } from '@/shared/config/locales';
import type { LocalizedText } from '@/lib/chat/tools/types';

/**
 * Maps app locale (URL/lang) to API/DB localized field keys.
 */
export const LOCALE_TO_API_KEY: Record<Locale, string> = {
  en: 'en_US',
  ko: 'ko_KR',
  th: 'th_TH',
  'zh-Hant': 'zh_TW',
  ja: 'ja_JP',
  hi: 'hi_IN',
  tl: 'tl_PH',
  ar: 'ar_SA',
};

/** Fallback order when preferred locale has no value. */
const API_KEYS_FALLBACK_ORDER = [
  'ko_KR',
  'en_US',
  'th_TH',
  'zh_TW',
  'ja_JP',
  'hi_IN',
  'tl_PH',
  'ar_SA',
] as const;

/**
 * Picks the best string from LocalizedText for display.
 * When preferredLocale is given, that language is used first; otherwise uses fallback order.
 */
export function pickLocaleName(
  json: LocalizedText,
  preferredLocale?: Locale
): string {
  if (json == null) return '';
  if (typeof json === 'string') return json;
  if (typeof json !== 'object' || Array.isArray(json)) return '';

  const keysToTry =
    preferredLocale != null
      ? [LOCALE_TO_API_KEY[preferredLocale], ...API_KEYS_FALLBACK_ORDER]
      : [...API_KEYS_FALLBACK_ORDER];

  for (const key of keysToTry) {
    const v = json[key];
    if (typeof v === 'string' && v.trim()) return v;
  }
  const first = Object.values(json).find((v) => typeof v === 'string');
  return typeof first === 'string' ? first : '';
}
